import { objectIsEmpty } from 'core/utils/objects';
import { FormikValues } from 'formik';
import { get, isArray, isBoolean, isEqual, isObject, isString, some, uniqBy } from 'lodash';
import { DeclarationCustomer } from 'store/declarations/common/declaration-customer';
import {
    Declaration,
    GeneratedDeclarationDocumentStatus,
    GeneratedDeclarationDocumentType,
} from 'store/declarations/declaration';
import { DeclarationExternalEntity } from 'store/declarations/enums/common/declaration-external-entity';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { DeclarationName } from 'store/declarations/enums/common/declaration-name';
import { DeclarationStatuses } from 'store/declarations/enums/common/declaration-status';
import {
    EnsGoodsShipmentItem,
    IrelandEntrySummaryDeclaration,
} from 'store/declarations/ireland/entry-summary-declaration';
import { IrelandExportDeclaration, IrelandExportItem } from 'store/declarations/ireland/export-declaration';
import { IrelandH7ImportDeclaration } from 'store/declarations/ireland/h7-import-declaration';
import { IrelandImportDeclaration } from 'store/declarations/ireland/import-declaration';
import { CdsDeclaration } from 'store/declarations/uk/cds-declaration';
import { ArrivalAtExitDeclaration } from '../../../store/declarations/ireland/arrival-at-exit-declaration';
import { ElectronicTransportDocument } from '../../../store/declarations/ireland/electronic-transport-document';
import { NctsDeclaration } from '../../../store/declarations/ireland/ncts-declaration';
import { IrelandPbnRecord } from '../../../store/declarations/ireland/pbn-declaration';
import { IrelandTemporaryStorageDeclaration } from '../../../store/declarations/ireland/temporary-storage-declaration';
import { UkGvmsRecord } from '../../../store/declarations/uk/gvms-declaration';
import { DeclarationFieldName } from '../common/declarationType';
import { CDSExportMessageTypes, DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import { ICS2EnsDeclaration } from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { JobResponse } from 'store/jobs/job';
import { AIS_MAX_ITEMS_PER_DECLARATION } from 'core/utils/constants';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import {
    generateIrelandClearanceDraft,
    generateIrelandSadDraft,
    getIrelandClearanceDraft,
    getIrelandSadDraft,
} from 'store/declarations/client';

export const disableSaveOrSubmitDeclarationButton = (status: DeclarationStatuses) =>
    status !== DeclarationStatuses.DRAFT &&
    status !== DeclarationStatuses.INVALID &&
    status !== DeclarationStatuses.REJECTED &&
    status !== DeclarationStatuses.DECLARATION_REJECTED &&
    status !== DeclarationStatuses.ICS2_REJECTED;

export const canDeclarationBeInvalidated = (status: DeclarationStatuses | undefined) => {
    if (!status) return false;
    return (
        status === DeclarationStatuses.REGISTERED ||
        status === DeclarationStatuses.ACCEPTED ||
        status === DeclarationStatuses.INSUFFICIENT_FUNDS ||
        status === DeclarationStatuses.RELEASED ||
        status === DeclarationStatuses.GOODS_RELEASED_FOR_EXPORT ||
        status === DeclarationStatuses.RISKED ||
        status === DeclarationStatuses.AWAITING_RISK ||
        status === DeclarationStatuses.UNDER_PAYMENT ||
        status === DeclarationStatuses.UNDER_CONTROL ||
        status === DeclarationStatuses.GOODS_READY_TO_BE_RELEASED
    );
};

// An ENS can only be consulted (IE3Q05) once it is lodged with customs, so the statuses that mean
// it never reached ICS2, or no longer exists there, are excluded rather than listing every other one.
export const canEnsBeConsulted = (status: DeclarationStatuses | undefined) => {
    if (!status) return false;
    return (
        status !== DeclarationStatuses.DRAFT &&
        status !== DeclarationStatuses.ICS2_REJECTED &&
        status !== DeclarationStatuses.ICS2_INVALIDATED &&
        status !== DeclarationStatuses.ICS2_PENDING_LINKING
    );
};

/** Mirrors the BLOCKED set in Ics2InvalidationPolicy.java on declarations-service - keep them in step. */
const ensInvalidationBlockedStatuses = [
    DeclarationStatuses.ICS2_RECEIVED,
    DeclarationStatuses.ICS2_REJECTED,
    DeclarationStatuses.ICS2_INVALIDATED,
    DeclarationStatuses.ICS2_PRESENTED,
    DeclarationStatuses.ICS2_UNDER_CONTROL,
    DeclarationStatuses.ICS2_RELEASED,
    DeclarationStatuses.ICS2_NOT_RELEASED,
] as DeclarationStatuses[];

/**
 * Optimistic: it cannot see a notified control or a request already in flight, so the
 * invalidation-eligibility endpoint stays the authority - confirm there before submitting.
 */
export const canEnsBeInvalidated = (declaration: Declaration | undefined) => {
    const status = declaration?.status;
    if (!status || !declaration.mrn) return false;

    return status.startsWith('ICS2_') && !ensInvalidationBlockedStatuses.includes(status);
};

const ensAmendableStatuses = [
    DeclarationStatuses.ICS2_REGISTERED,
    DeclarationStatuses.ICS2_ACCEPTED,
    DeclarationStatuses.ICS2_AMENDED,
    DeclarationStatuses.ICS2_PENDING_LINKING,
    DeclarationStatuses.ICS2_LINKED,
    DeclarationStatuses.ICS2_PENDING_COMPLETENESS,
    DeclarationStatuses.ICS2_COMPLETE,
    DeclarationStatuses.ICS2_CONSIDERED_COMPLETE_FOR_RA,
    DeclarationStatuses.ICS2_READY_FOR_FULL_RA,
    DeclarationStatuses.ICS2_COMPLETE_NO_RISK,
    DeclarationStatuses.ICS2_COMPLETE_RISK_IDENTIFIED,
    DeclarationStatuses.ICS2_ASSESSMENT_COMPLETED,
    DeclarationStatuses.ICS2_PARTIALLY_ARRIVED,
    DeclarationStatuses.ICS2_ARRIVED,
] as DeclarationStatuses[];

/** Once customs notifies a control the filing can no longer be amended (error 72). */
const ics2ControlNotificationTypes = ['IE3N08', 'IE3N09'];

/**
 * An amendment references the original MRN (IE3Axx/MRN is mandatory under R3004), so this only
 * becomes true once customs has registered the filing - not while it is merely dispatched.
 */
export const canEnsBeAmended = (declaration: Declaration | undefined) => {
    const status = declaration?.status;
    if (!status || !declaration.mrn || !ensAmendableStatuses.includes(status)) return false;

    return !declaration.ics2ENSDeclaration?.notifications?.some(
        ({ messageType }) => messageType && ics2ControlNotificationTypes.includes(messageType)
    );
};

const cdsCompletedStatuses = [
    DeclarationStatuses.COMPLETED_SUCCESSFULLY,
    DeclarationStatuses.DECLARATION_CLEARED,
] as DeclarationStatuses[];

export const isCompletedUkExportB1Declaration = (declaration: Declaration | undefined) =>
    declaration?.declarationExternalEntity === DeclarationExternalEntity.CDS &&
    declaration?.declarationInternalType === DeclarationInternalType.EXPORT &&
    declaration?.cdsDeclaration?.messageType === CDSExportMessageTypes.B1 &&
    cdsCompletedStatuses.includes(declaration.status);

// Declarations that are finished with and should show the Summary tab with their final documents.
export const declarationIsCompleted = (declaration: Declaration | undefined) =>
    declaration?.status === DeclarationStatuses.RELEASED ||
    declaration?.status === DeclarationStatuses.GOODS_RELEASED_FOR_EXPORT ||
    isCompletedUkExportB1Declaration(declaration);

export type DeclarationsPayloads =
    | CdsDeclaration
    | IrelandImportDeclaration
    | IrelandH7ImportDeclaration
    | IrelandExportDeclaration
    | IrelandEntrySummaryDeclaration
    | UkGvmsRecord
    | IrelandEntrySummaryDeclaration
    | ElectronicTransportDocument
    | IrelandTemporaryStorageDeclaration
    | ArrivalAtExitDeclaration
    | IrelandPbnRecord
    | NctsDeclaration
    | ICS2EnsDeclaration
    | undefined;

export interface DescriptionOfGoodsInformation {
    numberOfItems: number;
    netMass: number;
    grossMass: number;
    numberOfPackages: number;
}

const EMPTY_DESCRIPTION_OF_GOODS: Readonly<DescriptionOfGoodsInformation> = {
    numberOfItems: 0,
    netMass: 0.0,
    grossMass: 0.0,
    numberOfPackages: 0.0,
};

export const getDeclarationPayloadItems = (declaration: any) => {
    if (!declaration) return null;

    return (
        declaration?.goodsShipment?.goodsShipmentItem ??
        declaration?.goodsShipment?.governmentAgencyGoodsItem ??
        declaration?.goodsShipment?.goodsItem ??
        declaration?.goodsShipment?.goodsItems ??
        declaration?.goodsShipment?.goodsShipmentItems ??
        declaration?.goodsShipment?.goodsItem ??
        declaration?.goodsShipment?.goodsShipmentItem ??
        declaration?.consignment?.houseConsignment?.map((hc: any) => hc.consignmentItem).flat() ??
        declaration?.cdsGovernmentAgencyGoodsItems ??
        null
    );
};

export const getDeclarationItems = (declaration: Declaration | undefined): any | null => {
    if (!declaration) return null;

    return (
        declaration.irelandImportDeclaration?.goodsShipment?.goodsShipmentItem ??
        declaration.irelandH7ImportDeclaration?.goodsShipment?.governmentAgencyGoodsItem ??
        declaration.ieExportDeclaration?.goodsShipment?.goodsItem ??
        declaration.entrySummaryDeclaration?.goodsShipment?.goodsItems ??
        declaration.ieImportElectronicTransportDocument?.goodsShipment?.goodsShipmentItem ??
        declaration.ieArrivalAtExitDeclaration?.goodsShipment?.goodsItem ??
        declaration.ieImportTemporaryStorageDeclaration?.goodsShipment?.goodsShipmentItem ??
        declaration.preBoardingNotification ??
        declaration.ieNctsDeclaration?.consignment.houseConsignment.map((hc) => hc.consignmentItem).flat() ??
        declaration.ukNctsDeclaration?.consignment.houseConsignment.map((hc) => hc.consignmentItem).flat() ??
        declaration.cdsDeclaration?.cdsGovernmentAgencyGoodsItems ??
        declaration.gvmsDeclaration
    );
};

export const getDeclarationFieldName = (declaration: Declaration | undefined): DeclarationFieldName | null => {
    if (!declaration) return null;

    if (declaration.irelandImportDeclaration) return DeclarationFieldName.IE_IMPORT_H1;
    if (declaration.irelandH7ImportDeclaration) return DeclarationFieldName.IE_IMPORT_H7;
    if (declaration.ieExportDeclaration) return DeclarationFieldName.IE_EXPORT;
    if (declaration.entrySummaryDeclaration) return DeclarationFieldName.IE_ENS;
    if (declaration.ieImportElectronicTransportDocument) return DeclarationFieldName.IE_ETD;
    if (declaration.ieArrivalAtExitDeclaration) return DeclarationFieldName.IE_ARRIVAL;
    if (declaration.ieImportTemporaryStorageDeclaration) return DeclarationFieldName.IE_TSD;
    if (declaration.preBoardingNotification) return DeclarationFieldName.PBN;
    if (declaration.ieNctsDeclaration) return DeclarationFieldName.IE_NCTS;
    if (declaration.ukNctsDeclaration) return DeclarationFieldName.UK_NCTS;
    if (declaration.cdsDeclaration) return DeclarationFieldName.UK;
    if (declaration.gvmsDeclaration) return DeclarationFieldName.GVMS;

    return null;
};

export const getDeclarationType = (declaration: Declaration | undefined) => {
    const declarationType = getDeclarationFieldName(declaration);

    switch (declarationType) {
        case DeclarationFieldName.IE_IMPORT_H1:
        case DeclarationFieldName.IE_IMPORT_H7:
        case DeclarationFieldName.IE_TSD:
            return DeclarationTypes.AIS;
        case DeclarationFieldName.IE_ENS:
            return DeclarationTypes.ENS;
        case DeclarationFieldName.IE_ETD:
            return DeclarationTypes.ETD;
        case DeclarationFieldName.IE_EXPORT:
            return DeclarationTypes.AES;
        case DeclarationFieldName.IE_NCTS:
            return DeclarationTypes.NCTS;
        case DeclarationFieldName.UK:
            return DeclarationTypes.CDS;
        case DeclarationFieldName.UK_NCTS:
            return DeclarationTypes.NCTS;
        default:
            return undefined;
    }
};

export const getTransformedCountry = (country?: string) => {
    switch (country) {
        case 'IRELAND':
        case 'REVENUE':
            return 'ireland';
        case 'EU':
        case 'ICS2':
            return 'eu';
        case 'UK':
        case 'CDS':
        case 'HMRC':
        default:
            return 'uk';
    }
};

export const getDeclarationTypeByProductTemplate = (productTemplateType: string | undefined) => {
    switch (productTemplateType) {
        case 'IE_IMPORT_H1':
        case 'IE_IMPORT_H7':
            return DeclarationTypes.AIS;
        case 'IE_ENS':
            return DeclarationTypes.ENS;
        case 'IE_EXPORT':
            return DeclarationTypes.AES;
        case 'IE_NCTS':
            return DeclarationTypes.NCTS;
        case 'UK':
        case 'CDS_IMPORT':
        case 'CDS_EXPORT':
            return DeclarationTypes.CDS;
        case 'UK_NCTS':
            return DeclarationTypes.NCTS;
        default:
            return undefined;
    }
};

export const getDeclarationTypeByTemplate = (country: string | undefined, type: string | undefined) => {
    if (!country || !type) return undefined;

    switch (country) {
        case 'IRELAND':
            switch (type) {
                case 'IMPORT':
                    return 'AIS';
                case 'EXPORT':
                    return 'AES';
                case 'ENS':
                    return 'ENS';
                case 'NCTS':
                    return 'NCTS';
                default:
                    return undefined;
            }
        case 'UK':
            switch (type) {
                case 'IMPORT':
                    return 'CDS';
                case 'EXPORT':
                    return 'CDS';
                case 'NCTS':
                    return 'NCTS';
                default:
                    return undefined;
            }
        case 'EU':
            switch (type) {
                case 'ENS':
                    return 'ICS2';
                default:
                    return undefined;
            }
        default:
            return undefined;
    }
};

export const getDeclarationItemsPath = (declaration: Declaration | undefined): string | null => {
    if (!declaration) return null;

    if (declaration.irelandImportDeclaration) return 'goodsShipment.goodsShipmentItem';
    if (declaration.irelandH7ImportDeclaration) return 'goodsShipment.governmentAgencyGoodsItem';
    if (declaration.ieExportDeclaration) return 'goodsShipment.goodsItem';
    if (declaration.entrySummaryDeclaration) return 'goodsShipment.goodsItem';
    if (declaration.ieImportElectronicTransportDocument) return 'goodsShipment.goodsShipmentItems';
    if (declaration.ieArrivalAtExitDeclaration) return 'goodsShipment.goodsItem';
    if (declaration.ieImportTemporaryStorageDeclaration) return 'goodsShipment.goodsShipmentItem';
    if (declaration.preBoardingNotification) return '';
    if (declaration.ieNctsDeclaration || declaration.ukNctsDeclaration)
        return 'consignment.houseConsignment.0.consignmentItem'; // TODO DS
    if (declaration.cdsDeclaration) return 'cdsGovernmentAgencyGoodsItems';
    if (declaration.gvmsDeclaration) return '';

    return null;
};

export const getDeclarationPayload = (declaration: Declaration | undefined): DeclarationsPayloads | null => {
    if (!declaration) return null;

    const payload =
        declaration.irelandImportDeclaration ??
        declaration.irelandH7ImportDeclaration ??
        declaration.ieExportDeclaration ??
        declaration.entrySummaryDeclaration ??
        declaration.ieImportElectronicTransportDocument ??
        declaration.ieArrivalAtExitDeclaration ??
        declaration.ieImportTemporaryStorageDeclaration ??
        declaration.preBoardingNotification ??
        declaration.ieNctsDeclaration ??
        declaration.ukNctsDeclaration ??
        declaration.cdsDeclaration ??
        declaration.gvmsDeclaration ??
        declaration.ics2ENSDeclaration;

    return payload;
};

export const getMessageTypeByDeclaration = (declaration: Declaration) => {
    if (declaration.declarationExternalEntity === DeclarationExternalEntity.REVENUE) {
        if (declaration.declarationInternalType === DeclarationInternalType.IMPORT) {
            return declaration.irelandImportDeclaration
                ? declaration.irelandImportDeclaration?.messageType
                : DeclarationName.H7;
        } else if (declaration.declarationInternalType === DeclarationInternalType.EXPORT) {
            return declaration.ieExportDeclaration?.messageType;
        } else if (declaration.declarationInternalType === DeclarationInternalType.ENS) {
            return DeclarationName.ENS;
        } else if (declaration.declarationInternalType === DeclarationInternalType.ETD) {
            return declaration?.ieImportElectronicTransportDocument?.declaration?.messageType;
        } else if (declaration.declarationInternalType === DeclarationInternalType.ARRIVAL) {
            return DeclarationName.ARRIVAL;
        } else if (declaration.declarationInternalType === DeclarationInternalType.TEMPORARY) {
            return declaration?.ieImportTemporaryStorageDeclaration?.declaration?.msgType;
        } else if (declaration?.declarationInternalType === DeclarationInternalType.PBN) {
            return '-';
        } else if (declaration?.declarationInternalType === DeclarationInternalType.NCTS) {
            return declaration?.ieNctsDeclaration?.messageType;
        }
    } else if (declaration.declarationExternalEntity === DeclarationExternalEntity.CDS) {
        return declaration.cdsDeclaration?.messageType;
    } else if (declaration.declarationExternalEntity === DeclarationExternalEntity.GVMS) {
        return declaration.gvmsDeclaration?.messageType;
    } else if (declaration.declarationExternalEntity === DeclarationExternalEntity.HMRC) {
        return declaration?.ukNctsDeclaration?.messageType;
    }

    // ICS2
    else if (declaration.declarationExternalEntity === DeclarationExternalEntity.ICS2) {
        return declaration.ics2ENSDeclaration?.messageType;
    }

    console.error(`Unhandled declaration internal type ${declaration.declarationInternalType}`);

    return '-';
};

export const externalEntityMap: Record<DeclarationExternalEntity, string> = {
    [DeclarationExternalEntity.REVENUE]: 'Irish',
    [DeclarationExternalEntity.CDS]: 'UK',
    [DeclarationExternalEntity.HMRC]: 'UK',
    [DeclarationExternalEntity.CHIEF]: 'UK',
    [DeclarationExternalEntity.GVMS]: 'UK',
    [DeclarationExternalEntity.ICS2]: 'EU',
};

export const getDeclarationCountry = (declaration: Declaration) => {
    if (declaration.declarationExternalEntity === DeclarationExternalEntity.REVENUE) {
        return 'IE';
    } else if (declaration.declarationExternalEntity === DeclarationExternalEntity.ICS2) {
        return 'EU';
    } else {
        return 'UK';
    }
};

export const getExporterName = (payload: {
    exporter?: DeclarationCustomer;
    consignor?: DeclarationCustomer;
    revenueExportGoodsShipment?: IrelandExportItem;
    goodsShipment?: any;
}): string => {
    const exporter = getExporter(payload);
    return exporter?.name ?? exporter?.eori ?? 'N/A';
};

export const getExporter = (payload: {
    exporter?: DeclarationCustomer;
    goodsShipment?: {
        exporter?: DeclarationCustomer;
    };
}): DeclarationCustomer | undefined => {
    const exporter = payload?.exporter; // IE H1, IE Export, CDS Export
    const goodsShipmentExporter = payload?.goodsShipment?.exporter; // IE H7

    return exporter ?? goodsShipmentExporter ?? undefined;
};

export const getImporterName = (payload: {
    consignee?: DeclarationCustomer;
    goodsShipment?: { importer?: DeclarationCustomer; goodsItems?: EnsGoodsShipmentItem[] };
    revenueExportGoodsShipment?: IrelandExportItem;
}): string => {
    const importer = getImporter(payload);
    return importer?.name ?? importer?.eori ?? 'N/A';
};

export const getImporter = (payload: {
    goodsShipment?: {
        importer?: DeclarationCustomer;
    };
}): DeclarationCustomer | undefined => {
    const goodsShipmentImporter = payload?.goodsShipment?.importer; // AIS, CDS

    return goodsShipmentImporter ?? undefined;
};

export const getDeclarantName = (payload: {
    declarant?: DeclarationCustomer;
    representative?: DeclarationCustomer;
    representativeEori?: string;
    personLodgingTheSummaryDeclaration?: DeclarationCustomer;
}): string => {
    if (payload?.declarant) {
        return payload?.declarant?.name ?? payload?.declarant?.eori ?? 'N/A';
    } else {
        if (payload?.representative) {
            return payload.representative.name ?? payload.representative.eori ?? 'N/A';
        } else if (payload?.representativeEori) {
            return payload.representativeEori ?? 'N/A';
        }

        if (payload?.personLodgingTheSummaryDeclaration) {
            return (
                payload.personLodgingTheSummaryDeclaration.name ??
                payload.personLodgingTheSummaryDeclaration.eori ??
                'N/A'
            );
        }
    }
    return 'N/A';
};

export const getReferenceNumber = (declaration: Declaration) => {
    return declaration?.referenceNumber ?? 'N/A';
};

export const getMrn = (declaration: Declaration) => {
    return declaration?.mrn;
};

export const getJobPath = (jobId?: string, manifestId?: string) =>
    manifestId ? `/manifests/${manifestId}/${jobId}` : `/jobs/${jobId}`;

export type JobReferenceLink = {
    jobId: string;
    jobReference: string;
    path: string | undefined;
};

type PartialJobReference = { jobId?: string | null; jobReference?: string | null };

/** A job is only worth showing once we have the job id and the reference */
const isCompleteJobReference = (source: PartialJobReference): source is { jobId: string; jobReference: string } =>
    Boolean(source.jobId && source.jobReference);

/**
 * An ICS2 ENS declaration is generated from manifest jobs and keeps their references on itself —
 * F11 groups several of them, one per house consignment — so `declaration.jobId` is not the whole
 * picture, and every one of those jobs lives under the manifest the declaration came from. Any
 * other declaration belongs to the single job it was created under.
 */
export const getJobReferenceLinks = (declaration?: Declaration, job?: JobResponse): JobReferenceLink[] => {
    const ens = declaration?.ics2ENSDeclaration;

    if (ens) {
        const manifestId = ens.sourceManifestId;

        const sourceJobs: PartialJobReference[] = (ens.houseConsignments ?? []).map((house) => ({
            jobId: house.sourceJobId,
            jobReference: house.jobReferenceId,
        }));

        const ensJobs = sourceJobs.length
            ? sourceJobs
            : [{ jobId: ens.sourceManifestJobId, jobReference: ens.jobReferenceId }];

        return uniqBy(
            ensJobs.filter(isCompleteJobReference).map(({ jobId, jobReference }) => ({
                jobId,
                jobReference,
                path: manifestId ? getJobPath(jobId, manifestId) : undefined,
            })),
            'jobId'
        );
    }

    const declarationJob = { jobId: job?.id, jobReference: job?.referenceId };

    if (!isCompleteJobReference(declarationJob)) return [];

    return [
        {
            ...declarationJob,
            path: getJobPath(declarationJob.jobId, job?.manifest?.id),
        },
    ];
};

export const getLrn = (declaration: Declaration, lrnPath: string) => {
    const payload = getDeclarationPayload(declaration);
    return get(payload, lrnPath);
};

const getIrelandH1ItemsInfo = (declaration: Declaration): DescriptionOfGoodsInformation => {
    const items = declaration?.irelandImportDeclaration?.goodsShipment?.goodsShipmentItem;
    if (items) {
        const numberOfItems = items?.length ?? 0;
        const toNumber = (value: any) => {
            const number = parseFloat(value);
            if (Number.isNaN(number)) return null;
            return number;
        };
        const netMass = items.map((i) => toNumber(i.goodsInformation?.netMass) ?? 0.0).reduce((a, b) => a + b, 0);
        const grossMass = items.map((i) => toNumber(i.goodsInformation?.netMass) ?? 0.0).reduce((a, b) => a + b, 0);

        const numberOfPackages = items
            .map((i) =>
                i.goodsInformation?.packaging
                    ? i.goodsInformation?.packaging.map((p) => +(p.packageNumber ?? 0)).reduce((a, b) => a + b, 0)
                    : 0
            )
            .reduce((a, b) => a + b, 0);

        return { numberOfItems, netMass, grossMass, numberOfPackages };
    }
    return EMPTY_DESCRIPTION_OF_GOODS;
};

const getIrelandH7ItemsInfo = (declaration: Declaration): DescriptionOfGoodsInformation => {
    const items = declaration?.irelandH7ImportDeclaration?.goodsShipment?.governmentAgencyGoodsItem;
    if (items) {
        const numberOfItems = items?.length ?? 0;
        const netMass = 0.0;
        const grossMass = declaration?.irelandH7ImportDeclaration?.goodsShipment?.grossMass ?? 0.0;
        const numberOfPackages = items.map((i) => +(i?.packagingNumberOfPackages ?? 0)).reduce((a, b) => a + b, 0);
        return { numberOfItems, netMass, grossMass, numberOfPackages };
    }
    return EMPTY_DESCRIPTION_OF_GOODS;
};

const getIrelandExportItemsInfo = (declaration: Declaration) => {
    const items = declaration?.ieExportDeclaration?.goodsShipment?.goodsItem;
    if (items) {
        const numberOfItems = items?.length ?? 0;
        const netMass = items?.map((i) => +(i?.commodity?.goodsMeasure?.netMass ?? 0.0)).reduce((a, b) => a + b, 0);
        const grossMass = items.map((i) => +(i?.commodity?.goodsMeasure?.grossMass ?? 0.0)).reduce((a, b) => a + b, 0);
        const numberOfPackages = items
            .map((i) => +(i?.packaging?.reduce((acc, current) => +(current.numberOfPackages ?? 0) + acc, 0) ?? 0))
            .reduce((a, b) => a + b, 0);
        return { numberOfItems, netMass, grossMass, numberOfPackages };
    }
    return EMPTY_DESCRIPTION_OF_GOODS;
};

const getIrelandEnsItemsInfo = (declaration: Declaration) => {
    const items = declaration?.entrySummaryDeclaration?.goodsShipment?.goodsItems;
    if (items) {
        const numberOfItems = items?.length ?? 0;
        const netMass = 0.0;
        const grossMass = declaration?.entrySummaryDeclaration?.totalGrossMass ?? 0.0;
        const numberOfPackages = items
            .map((i) =>
                i.goodsPackaging ? i.goodsPackaging.map((p) => +(p.marks ?? 0)).reduce((a, b) => a + b, 0) : 0
            )
            .reduce((a, b) => a + b, 0);
        return { numberOfItems, netMass, grossMass, numberOfPackages };
    }
    return EMPTY_DESCRIPTION_OF_GOODS;
};

const getIrelandArrivalItemsInfo = (declaration: Declaration) => {
    const items = declaration?.ieArrivalAtExitDeclaration?.goodsShipment?.goodsItem;
    if (items) {
        const numberOfItems = items?.length ?? 0;
        const netMass = items?.map((i) => +(i?.commodity?.goodsMeasure?.netMass ?? 0.0)).reduce((a, b) => a + b, 0);
        const grossMass = items.map((i) => +(i?.commodity?.goodsMeasure?.grossMass ?? 0.0)).reduce((a, b) => a + b, 0);
        const numberOfPackages = items
            .map((i) => +(i?.packaging?.reduce((acc, current) => +(current.numberOfPackages ?? 0) + acc, 0) ?? 0))
            .reduce((a, b) => a + b, 0);
        return { numberOfItems, netMass, grossMass, numberOfPackages };
    }
    return EMPTY_DESCRIPTION_OF_GOODS;
};

const getIrelandTemporaryItemsInfo = (declaration: Declaration) => {
    const items = declaration?.ieImportTemporaryStorageDeclaration?.goodsShipment?.goodsShipmentItem;
    if (items) {
        const numberOfItems = items?.length ?? 0;
        const netMass = 0.0;
        const grossMass = items.map((i) => +(i?.goodsInformation.grossMass65 ?? 0.0)).reduce((a, b) => a + b, 0);
        const numberOfPackages = items
            .map(
                (i) =>
                    +(
                        i?.goodsInformation.packaging?.reduce(
                            (acc, current) => +(current.packageNumber610 ?? 0) + acc,
                            0
                        ) ?? 0
                    )
            )
            .reduce((a, b) => a + b, 0);
        return { numberOfItems, netMass, grossMass, numberOfPackages };
    }
    return EMPTY_DESCRIPTION_OF_GOODS;
};

const getIrelandEtdItemsInfo = (declaration: Declaration) => {
    const items = declaration?.ieImportElectronicTransportDocument?.goodsShipment?.goodsShipmentItem;
    if (items) {
        const numberOfItems = items?.length ?? 0;
        const netMass = 0.0;
        const grossMass = items.map((i) => +(i?.goodsInformation?.grossMass65 ?? 0.0)).reduce((a, b) => a + b, 0);
        const numberOfPackages = items
            .map(
                (i) =>
                    +(
                        i?.goodsInformation?.packaging?.reduce(
                            (acc, current) => +(current.packageNumber610 ?? 0) + acc,
                            0
                        ) ?? 0
                    )
            )
            .reduce((a, b) => a + b, 0);
        return { numberOfItems, netMass, grossMass, numberOfPackages };
    }
    return EMPTY_DESCRIPTION_OF_GOODS;
};

const getIrelandNctsItemsInfo = (declaration: Declaration): DescriptionOfGoodsInformation => {
    const items = declaration?.ieNctsDeclaration?.consignment?.houseConsignment;
    if (items) {
        const numberOfItems =
            items?.reduce((total, consignment) => {
                return total + consignment.consignmentItem.length;
            }, 0) ?? 0;

        const netMass =
            items.reduce((total, consignment) => {
                const consignmentTotal =
                    consignment.consignmentItem.reduce((subTotal, item) => {
                        const netMass = item.commodity?.goodsMeasure?.netMass || 0;
                        return subTotal + netMass;
                    }, 0) || 0;
                return total + consignmentTotal;
            }, 0) || 0;

        const grossMass = declaration?.ieNctsDeclaration?.consignment?.grossMass ?? 0.0;

        const numberOfPackages =
            items?.reduce((total, consignment) => {
                const consignmentTotal =
                    consignment?.consignmentItem?.reduce((subTotal, item) => {
                        const packagingLength = item.packaging ? item.packaging.length : 0;
                        return subTotal + packagingLength;
                    }, 0) || 0;

                return total + consignmentTotal;
            }, 0) || 0;

        return { numberOfItems, netMass, grossMass, numberOfPackages };
    }
    return EMPTY_DESCRIPTION_OF_GOODS;
};

const getUkNctsItemsInfo = (declaration: Declaration): DescriptionOfGoodsInformation => {
    const items = declaration?.ukNctsDeclaration?.consignment?.houseConsignment;
    if (items) {
        const numberOfItems =
            items?.reduce((total, consignment) => {
                return total + consignment.consignmentItem.length;
            }, 0) ?? 0;

        const netMass =
            items.reduce((total, consignment) => {
                const consignmentTotal =
                    consignment.consignmentItem.reduce((subTotal, item) => {
                        const netMass = item.commodity?.goodsMeasure?.netMass || 0;
                        return subTotal + netMass;
                    }, 0) || 0;
                return total + consignmentTotal;
            }, 0) || 0;

        const grossMass = declaration?.ukNctsDeclaration?.consignment?.grossMass ?? 0.0;

        const numberOfPackages =
            items?.reduce((total, consignment) => {
                const consignmentTotal =
                    consignment?.consignmentItem?.reduce((subTotal, item) => {
                        const packagingLength = item.packaging ? item.packaging.length : 0;
                        return subTotal + packagingLength;
                    }, 0) || 0;

                return total + consignmentTotal;
            }, 0) || 0;

        return { numberOfItems, netMass, grossMass, numberOfPackages };
    }
    return EMPTY_DESCRIPTION_OF_GOODS;
};

const getUkExportItemsInfo = (declaration: Declaration) => {
    const items = declaration?.cdsDeclaration?.cdsGovernmentAgencyGoodsItems?.map(
        ({ governmentAgencyGoodsItem }) => governmentAgencyGoodsItem
    );
    if (items) {
        const numberOfItems = items?.length ?? 0;
        const numberOfPackages = items
            .map(
                (i) =>
                    i?.packaging?.reduce((acc, { quantityQuantity }) => acc + (Number(quantityQuantity) || 0), 0) ?? 0
            )
            .reduce((acc, current) => acc + current, 0);
        const netMass = items
            .map((i) => i?.commodity?.goodsMeasure?.netNetWeightMeasure ?? 0.0)
            .reduce((a: any, b: any) => a + b, 0);
        const grossMass = items
            .map((i) => i?.commodity?.goodsMeasure?.grossMassMeasure ?? 0.0)
            .reduce((a: any, b: any) => a + b, 0);
        return { numberOfItems, netMass, grossMass, numberOfPackages };
    }
    return EMPTY_DESCRIPTION_OF_GOODS;
};

export const getDescriptionOfGoods = (declaration: Declaration): DescriptionOfGoodsInformation => {
    if (declaration.declarationExternalEntity === DeclarationExternalEntity.REVENUE) {
        if (declaration.declarationInternalType === DeclarationInternalType.IMPORT) {
            return declaration.irelandImportDeclaration
                ? getIrelandH1ItemsInfo(declaration)
                : getIrelandH7ItemsInfo(declaration);
        } else if (declaration.declarationInternalType === DeclarationInternalType.EXPORT) {
            return getIrelandExportItemsInfo(declaration);
        } else if (declaration.declarationInternalType === DeclarationInternalType.ENS) {
            return getIrelandEnsItemsInfo(declaration);
        } else if (declaration.declarationInternalType === DeclarationInternalType.NCTS) {
            return getIrelandNctsItemsInfo(declaration);
        } else if (declaration.declarationInternalType === DeclarationInternalType.ARRIVAL) {
            return getIrelandArrivalItemsInfo(declaration);
        } else if (declaration.declarationInternalType === DeclarationInternalType.TEMPORARY) {
            return getIrelandTemporaryItemsInfo(declaration);
        } else if (declaration.declarationInternalType === DeclarationInternalType.ETD) {
            return getIrelandEtdItemsInfo(declaration);
        }
    } else if (declaration.declarationExternalEntity === DeclarationExternalEntity.CDS) {
        return getUkExportItemsInfo(declaration);
    } else if (declaration.declarationExternalEntity === DeclarationExternalEntity.HMRC) {
        return getUkNctsItemsInfo(declaration);
    }

    return EMPTY_DESCRIPTION_OF_GOODS;
};

const getObjectDifferences = (data: FormikValues, oldData: FormikValues) => {
    const record: any = {};
    Object.keys(data).forEach((key: string) => {
        const old = oldData[key] ?? {};
        const current = data[key] ?? {};
        if (isString(current) || Number.isFinite(current) || isBoolean(current) || !current) {
            if (!isEqual(old, current)) {
                record[key] = current;
            }
        } else {
            if (!isEqual(current, old)) {
                record[key] = getObjectDifferences(current, old);
            }
        }
    });
    return record;
};

const handleEmptyObjects = (data: any, contains: boolean[]) => {
    Object.values(data).forEach((d) => {
        if (isObject(d)) {
            if (objectIsEmpty(d)) {
                contains.push(true);
            } else {
                handleEmptyObjects(d, contains);
            }
        } else {
            if (isArray(d)) {
                d.forEach((obj) => {
                    if (obj && Object.keys(obj).length === 0) {
                        contains.push(true);
                    }
                });
            }
        }
    });
};

/**
 * @param initialValue
 * @param currentValue
 * @returns
 * False if no changes are made on the declaration\
 * True if changes are made on the declaration
 */
// TODO CHECK LOGIC There is an issue with this implementation that is why in some places only isEqual is used for the comparison.
// In some occasions it says that the declaration is not changed but it actually it is.
export const declarationChanged = (initialValue: FormikValues, currentValue: FormikValues) => {
    const initial = { ...initialValue };
    const current = { ...currentValue };
    if (isEqual(initial, current)) {
        return false;
    } else {
        const diff = getObjectDifferences(current, initial);
        const containsEmptyObjects: [] = [];
        handleEmptyObjects(diff, containsEmptyObjects);
        if (containsEmptyObjects?.length) {
            if (containsEmptyObjects.every((o) => !!o === true)) {
                return false;
            } else {
                return true;
            }
        }
        return true;
    }
};

export const isDeclarationAmendable = (declaration: Declaration) => {
    const amendmentStatuses = [
        DeclarationStatuses.ACCEPTED,
        DeclarationStatuses.REGISTERED,
        DeclarationStatuses.AWAITING_RISK,
        DeclarationStatuses.INSUFFICIENT_FUNDS,
        DeclarationStatuses.UNDER_PAYMENT,
        DeclarationStatuses.RISKED,
        DeclarationStatuses.UNDER_CONTROL,
        DeclarationStatuses.RELEASED,
    ];

    const gvmsAmendmentStatuses = [
        DeclarationStatuses.OPEN,
        DeclarationStatuses.NOT_FINALISABLE,
        DeclarationStatuses.CHECKED_IN,
    ];

    const isAmendable = (statuses: DeclarationStatuses[], exclude = false) => {
        const hasSome = some(statuses, (status) => declaration?.status === status);
        return exclude ? !hasSome : hasSome;
    };

    if (declaration?.gvmsDeclaration) {
        return isAmendable(gvmsAmendmentStatuses);
    }

    return isAmendable(amendmentStatuses);
};

export const isDeclarationRefundRemissionRequested = (declaration: Declaration | undefined) => {
    const declarationType = declaration?.irelandImportDeclaration ?? declaration?.irelandH7ImportDeclaration;

    return Boolean(declarationType?.refundApplicationForDebtRemissionRequested);
};

export const isDeclarationRefundDepositRequested = (declaration: Declaration | undefined) => {
    const declarationType = declaration?.irelandImportDeclaration ?? declaration?.irelandH7ImportDeclaration;

    return Boolean(declarationType?.refundApplicationForDebtDepositRequested);
};

export const getDeclarationKey = (
    declaration: Pick<Declaration, 'declarationExternalEntity' | 'declarationInternalType' | 'cdsDeclaration'> & {
        h7?: boolean;
        formType?: string;
    }
) => {
    const getIrelandDeclarationType = () => {
        switch (declaration.declarationInternalType) {
            case DeclarationInternalType.IMPORT:
                if (declaration.h7 || declaration.formType === 'H7') {
                    return DeclarationFieldName.IE_IMPORT_H7;
                } else {
                    return DeclarationFieldName.IE_IMPORT_H1;
                }
            case DeclarationInternalType.EXPORT:
                return DeclarationFieldName.IE_EXPORT;
            case DeclarationInternalType.ENS:
                return DeclarationFieldName.IE_ENS;
            case DeclarationInternalType.ETD:
                return DeclarationFieldName.IE_ETD;
            case DeclarationInternalType.TEMPORARY:
                return DeclarationFieldName.IE_TSD;
            case DeclarationInternalType.ARRIVAL:
                return DeclarationFieldName.IE_ARRIVAL;
            case DeclarationInternalType.PBN:
                return DeclarationFieldName.PBN;
            case DeclarationInternalType.NCTS:
                return DeclarationFieldName.IE_NCTS;
            default:
                return null;
        }
    };

    switch (declaration.declarationExternalEntity) {
        case DeclarationExternalEntity.REVENUE:
            return getIrelandDeclarationType();

        case DeclarationExternalEntity.CDS:
            return DeclarationExternalEntity.CDS;
        case DeclarationExternalEntity.GVMS:
            return DeclarationFieldName.GVMS;
        case DeclarationExternalEntity.HMRC:
            return DeclarationFieldName.UK_NCTS;
        case DeclarationExternalEntity.ICS2:
            return DeclarationFieldName.ENS_ICS2;
        default:
            // shouldn't happen, or should show an error page
            throw Error('Declaration must have an external entity');
    }
};

// TODO: Handle other declaration types when needed
export const getPreferredPaymentMethod = (declaration: Declaration | undefined): string | undefined => {
    if (declaration?.irelandImportDeclaration) {
        return declaration.irelandImportDeclaration?.preferredPaymentMethod;
    }

    return undefined;
};

export interface ItemsCapInfo {
    isAtItemsCap: boolean;
    itemsCap: number | undefined;
}

export const checkForItemsCap = (
    country: string,
    internalType: string | undefined,
    declarationFormType: string | undefined,
    productsCount: number | undefined
): ItemsCapInfo => {
    const isAis =
        country.toLowerCase() === DeclarationCountry.IRELAND && internalType === DeclarationInternalType.IMPORT;

    if (isAis && declarationFormType === 'H1') {
        return {
            isAtItemsCap: (productsCount ?? 0) >= AIS_MAX_ITEMS_PER_DECLARATION,
            itemsCap: AIS_MAX_ITEMS_PER_DECLARATION,
        };
    }

    return { isAtItemsCap: false, itemsCap: undefined };
};

// SAD and clearance-slip documents only apply to Ireland AIS/AES declarations. FINAL is only
// available once the declaration is released; before that we generate and fetch a DRAFT.
export const getGeneratedDeclarationDocument = async (
    documentType: GeneratedDeclarationDocumentType,
    declaration: Declaration
): Promise<{ blob: Blob; fileName?: string; type?: string; status: GeneratedDeclarationDocumentStatus } | null> => {
    const declarationType = getDeclarationType(declaration);
    const isAis = declarationType === DeclarationTypes.AIS;
    const isAes = declarationType === DeclarationTypes.AES;

    if (!declaration.id || (!isAis && !isAes)) return Promise.resolve(null);

    const isReleased =
        declaration.status === DeclarationStatuses.RELEASED ||
        declaration.status === DeclarationStatuses.GOODS_RELEASED_FOR_EXPORT;

    const getDraft = documentType === 'SAD' ? getIrelandSadDraft : getIrelandClearanceDraft;

    if (isReleased) {
        const finalDocument = await getDraft(declaration.id, 'FINAL');
        return { ...finalDocument, status: 'FINAL' as const };
    }

    const generateDraft = documentType === 'SAD' ? generateIrelandSadDraft : generateIrelandClearanceDraft;

    // Generate the draft document based on the current state of the declaration
    await generateDraft(declaration.id);
    const draftDocument = await getDraft(declaration.id!, 'DRAFT');
    return { ...draftDocument, status: 'DRAFT' as const };
};
